<template>
  <div
    class="wishlist-button"
    @click="handleButtonClick"
  >
    <span
      class="wishlist-button__label text-desktop-regular-uc"
      v-text="labelString"
    />

    <button
      type="button"
      class="wishlist-button__button"
      :class="{'is-loaded': wishlistInit}"
      :aria-label="$string('product.wishlist.wishlist_button')"
    >
      <icon-bookmark-fill v-if="wishlistProduct" />
      <icon-bookmark v-else />
    </button>
  </div>
</template>

<script>

/**
 * Vue: Wishlist button (wishlist-button)
 * -----------------------------------------------------------------------------
 * Enables a product to be added and removed from a wishlist - displays the
 * wishlist status.
 *
 * @param {Boolean, Object} product - Shopify product object.
 *
 */
import { mapGetters } from 'vuex'

import IconBookmark from '~icons/misc/bookmark.svg'
import IconBookmarkFill from '~icons/misc/bookmark-fill.svg'

export default {
  name: 'WishlistButton',

  components: {
    IconBookmark,
    IconBookmarkFill,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      inWishlist: false,
      wishlistProduct: null,
      wishlistInit: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Returns the correct wishlist button label.
     */
    labelString() {
      return this.wishlistProduct
        ? this.$string('product.wishlist.remove')
        : this.$string('product.wishlist.add')
    },
  },

  watch: {
    product: {
      immediate: true,
      handler() {
        this.initWishlist()
      },
    },

    /*
     * Update wishlist button when overlay is closed
     */
    getActiveOverlay() {
      if (!this.getActiveOverlay) {
        this.initWishlist()
      }
    },
  },

  methods: {

    /**
     * Ensures Wishlist King has initiated before checking if the current
     * product is on the wishlist.
     */
    async initWishlist() {
      await new Promise((resolve) => {
        const wishlistInterval = window.setInterval(() => {
          if (typeof window.WishlistKing === 'undefined') {
            return
          }

          window.clearInterval(wishlistInterval)
          this.getWishlistProduct(this.product.id)
          resolve()
          this.wishlistInit = true
        }, this.$timing('quick'))
      })
    },

    /**
     * Handles adding a product to the wishlist.
     * @param {String} productId - ID for the main product.
     * @param {String} variantId - ID for the specific product variant.
     */
    async addToWishlist(productId = this.product.id, variantId = this.product.variants[0].id) {
      try {
        // eslint-disable-next-line no-undef
        const added = await window.WishlistKing.toolkit.addProduct(
          productId,
          variantId,
        )
        this.wishlistProduct = added
        this.handleGtm('add')
      } catch (error) {
        throw new Error('Failed to update wishlist', error)
      }
    },

    /**
     * Handles removing a product from the wishlist.
     * @param {String} wishlistProductId - wishlist item ID to be removed.
     */
    async removeFromWishlist(wishlistProductId) {
      try {
        const removed = await window.WishlistKing.toolkit.removeItem(wishlistProductId)

        if (removed.wishlist_state !== 'not-added') {
          return
        }
        this.wishlistProduct = null
        this.handleGtm('remove')
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     * Checks if the current product is on a wishlist.
     * @param {String} productId - ID for the main product.
     */
    async getWishlistProduct(productId) {
      try {
        this.wishlistProduct = await window.WishlistKing.toolkit.getProduct(productId)
      } catch (error) {
        throw new Error(error)
      }
    },

    /**
     * Handles the click event on the wishlist button.
     */
    handleButtonClick() {
      if (this.wishlistProduct) {
        this.removeFromWishlist(this.wishlistProduct.wishlist_item_id)
        return
      }

      this.addToWishlist()
    },

    handleGtm(action) {
      const dataLayer = window.dataLayer || []
      const { handle, id, price, title } = this.product

      if (action === 'add') {
        dataLayer.push({
          event: 'add_to_wishlist',
          addToWishlist: {
            url: document.location.pathname,
            value: price.amount,
            currency: price.currencyCode,
            items: [
              {
                handle,
                item_name: title,
                item_id: id,
              },
            ],
          },
        })
      } else {
        dataLayer.push({
          event: 'remove_from_wishlist',
          removeFromWishlist: {
            url: document.location.pathname,
            value: price.amount,
            currency: price.currencyCode,
            items: [
              {
                handle,
                item_name: title,
                item_id: id,
              },
            ],
          },
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import './wishlist-button';
</style>
